import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Car, Building2 } from 'lucide-react';
import { offerColorStyles, brandButtonProps } from '../utils/offerColor';

/**
 * Location details step: collects zip, park places, floor before creating the offer.
 * Used in StepperChatbotWidget between chat and offer overview.
 */
const LocationDetailsStep = ({
  onContinue,
  onBack,
  compact = false,
  showBackButton = true,
  primaryColor,
}) => {
  const { t } = useTranslation();
  const theme = offerColorStyles(primaryColor);
  const [zip, setZip] = useState('');
  const [parkPlaces, setParkPlaces] = useState('');
  const [floor, setFloor] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!zip.trim()) return;
    onContinue({
      zip: zip.trim(),
      parkPlaces: parkPlaces.trim() || undefined,
      floor: floor.trim() || undefined,
    });
  };

  const containerClass = compact ? 'p-4' : 'p-6';

  return (
    <div className={`${containerClass} overflow-y-auto max-h-[700px]`} style={{ '--offer-color': theme.color }}>
      <h2 className={`font-bold text-gray-900 mb-2 ${compact ? 'text-xl' : 'text-2xl'}`}>
        {t('stepper.locationDetailsTitle')}
      </h2>
      <p className="text-sm text-gray-600 mb-6">{t('stepper.locationDetailsDescription')}</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <MapPin className="inline h-4 w-4 mr-1" />
            {t('stepper.zipCode')} *
          </label>
          <input
            type="text"
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none offer-brand-input"
            placeholder={t('stepper.placeholderZip')}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Car className="inline h-4 w-4 mr-1" />
            {t('stepper.parkPlaces')}
          </label>
          <select
            value={parkPlaces}
            onChange={(e) => setParkPlaces(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none offer-brand-input"
          >
            <option value="">{t('stepper.selectOption')}</option>
            <option value="yes">{t('stepper.yes')}</option>
            <option value="no">{t('stepper.no')}</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Building2 className="inline h-4 w-4 mr-1" />
            {t('stepper.floor')}
          </label>
          <input
            type="text"
            value={floor}
            onChange={(e) => setFloor(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none offer-brand-input"
            placeholder={t('stepper.placeholderFloor')}
          />
        </div>

        <div className="flex justify-end gap-3 pt-6">
          {showBackButton && onBack && (
            <button
              type="button"
              onClick={onBack}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {t('common.back')}
            </button>
          )}
          <button
            type="submit"
            disabled={!zip.trim()}
            className="px-6 py-2 text-white rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            {...brandButtonProps(theme)}
          >
            {t('stepper.continue')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LocationDetailsStep;
